import { Link } from '@tanstack/react-router';
import { ArrowUpCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { seatLimitDetailsSchema, useBillingOverview, type SeatLimitDetails } from './billing-api';

/** 409 SEAT_LIMIT מהשרת (POST /team, PATCH /team/:id) → הפרטים, אחרת null. */
export function seatLimitOf(e: unknown): SeatLimitDetails | null {
  if (!(e instanceof ApiError) || e.code !== 'SEAT_LIMIT') return null;
  const parsed = seatLimitDetailsSchema.safeParse(e.details);
  return parsed.success ? parsed.data : { seatsUsed: 0, seatsAfter: 0, seatLimit: 0 };
}

/** "נגמרו המושבים" — עם קישור לשדרוג לבעלים, או "פנו לבעלים" לאחרים. */
export function SeatLimitPrompt({
  details,
  className,
  onDismiss,
}: {
  details: SeatLimitDetails;
  className?: string;
  onDismiss?: () => void;
}) {
  const { t } = useTranslation(['billing', 'common']);
  const { session } = useAuth();
  const isOwner = session?.user.role === 'OWNER';
  return (
    <div
      role="alert"
      className={cn(
        'relative space-y-1.5 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-fg',
        className,
      )}
    >
      {onDismiss ? (
        <button
          type="button"
          onClick={onDismiss}
          aria-label={t('common:actions.close')}
          className="absolute end-2 top-2 text-fg-subtle hover:text-fg"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
      <p className="font-semibold text-warning">{t('billing:seatLimit.title')}</p>
      {details.seatLimit > 0 ? (
        <p>
          {t('billing:seatLimit.body', {
            used: details.seatsUsed,
            limit: details.seatLimit,
            after: details.seatsAfter,
          })}
        </p>
      ) : null}
      {isOwner ? (
        <>
          <p>{t('billing:seatLimit.owner')}</p>
          <Link
            to="/settings/$tab"
            params={{ tab: 'billing' }}
            className="inline-flex items-center gap-1 font-semibold text-accent hover:underline"
          >
            <ArrowUpCircle className="size-3.5" aria-hidden />
            {t('billing:seatLimit.upgrade')}
          </Link>
        </>
      ) : (
        <p>{t('billing:seatLimit.ask')}</p>
      )}
    </div>
  );
}

/** "X מתוך Y מושבים בשימוש" בכותרת הצוות (בעלים ומנהל — מי ש-GET /billing פתוח לו). */
export function SeatsUsedBadge() {
  const { t } = useTranslation('billing');
  const { session } = useAuth();
  const role = session?.user.role;
  const overview = useBillingOverview(role === 'OWNER' || role === 'MANAGER');
  const seats = overview.data?.seats;
  if (!seats || seats.limit === null) return null;
  return (
    <p className="mt-1 text-2xs text-fg-subtle tabular-nums">
      {t('seatLimit.header', { used: seats.used, limit: seats.limit })}
    </p>
  );
}
